// eslint-disable-next-line no-unused-vars
import React from 'react';
import { bool, func, object, string } from 'prop-types';
import { withTranslation } from 'react-i18next';

import ProductWishlistItemComponent from 'Components/Products/ProductWishlistItem';
import ProductAbstract from 'Containers/ProductAbstract';
import withWishlist from 'AppSrc/hocs/withWishlist';
import withCart from 'AppSrc/hocs/withCart';
import { API_PRODUCTS } from 'Constants';
import withModals from 'AppSrc/hocs/withModals';
import withFetch from 'AppSrc/hocs/withFetch';
import withAnalytics from 'AppSrc/hocs/withAnalytics';
import withToastNotifications from 'AppSrc/hocs/withToastNotifications';

export class ProductWishlistItem extends ProductAbstract {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      loading: false,
      addToCartLoading: false,
      error: null,
      product: {
        ...this.state.product,
        productId: props.product.id,
        id: props.product.id
      }
    };
    if (
      props.product?.pictures?.length
      && (
        !this.state.product.picture
        || this.state.product.picture.includes('no-image-available')
      )
    ) {
      this.state.product.picture = props.product?.pictures?.at(0)?.urlOverlayPicture;
    }
    this.handleOnDelete = this.handleOnDelete.bind(this);
    this.handleOnChangeQuantity = this.handleOnChangeQuantity.bind(this);
    this.handleOnMoveToWishlist = this.handleOnMoveToWishlist.bind(this);
    this.handleOnAddToCart = this.handleOnAddToCart.bind(this);
    this.executeAddToCartFromWishlist = this.executeAddToCartFromWishlist.bind(this);
  }

  /**
   * Delete product from wishlist
   */
  async handleOnDelete() {
    const { wishlistContext, parentWishlist } = this.props;
    const { product } = this.state;
    this.setState({ loading: true, error: null });
    try {
      return await wishlistContext.removeProduct(parentWishlist.id, product.id);
    } catch (e) {
      this.setState({ error: e.message });
    } finally {
      this.setState({ loading: false });
    }
    return null;
  }

  /**
   * Update wishlist item qty
   * @param quantity
   */
  async handleOnChangeQuantity(quantity) {
    const { wishlistContext, parentWishlist } = this.props;
    const { product } = this.state;
    if (quantity === product.quantity) return;
    this.setState(prevState => ({
      ...prevState,
      loading: true,
      error: null,
      product: {
        ...prevState.product,
        quantity
      }
    }));
    try {
      await wishlistContext.updateProduct(parentWishlist.id, {
        id: product.id || product.productId,
        quantity
      });
    } catch (e) {
      this.setState(prevState => ({
        ...prevState,
        product: {
          ...prevState.product,
          quantity: product.quantity
        },
        error: e.message
      }));
    } finally {
      this.setState({ loading: false });
    }
  }

  /**
   * Move product from one list to another
   * @param {Object<string,string>} selectedItem
   */
  async handleOnMoveToWishlist({ selectedItem }) {
    // TODO: refactor this to use the new moveToWishlist method from context
    const { wishlistContext, toast, t, parentWishlist } = this.props;
    const { product } = this.state;
    this.setState({ loading: true, error: null });
    try {
      return await Promise.all([
        wishlistContext.updateProduct(parentWishlist.id, {
          id: product.id,
          quantity: 0
        }),
        this.handleOnAddToWishlist({ selectedItem })
      ]);
    } catch (err) {
      this.setState({ error: err.message });
      toast.addNotification({
        type: 'error',
        title: err.info?.errors?.at(0)?.message || t('wishlists.failedAddToWishlist')
      });
    } finally {
      this.setState({ loading: false });
    }
    return null;
  }

  /**
   * add wishlist item to cart
   * @returns {Promise<*|null>}
   */
  async handleOnAddToCart(defaultCurrency, withVat) {
    const { product } = this.state;
    const { modalsContext, analytics } = this.props;

    analytics.track('add_product_to_cart_from_wishlist', {
      product_id: product?.id,
      product_sku: product?.code,
    });

    if (!product.hasPrice) {
      modalsContext.setState(modalsContext.modalState.ADD_EXTERNAL_SUPPLIER_PRODUCT_TO_CART.key, {
        open: true,
        product,
        onConfirm: () => this.executeAddToCartFromWishlist(defaultCurrency, withVat)
      });
      return null;
    }

    return this.executeAddToCartFromWishlist(defaultCurrency, withVat);
  }

  /**
   * Execute the actual add to cart logic from wishlist
   * @returns {Promise<*|null>}
   */
  async executeAddToCartFromWishlist(defaultCurrency, withVat) {
    const { product } = this.state;
    const { cart, t, toast, fetch, modalsContext } = this.props;
    const prodId = (product.id || product.productId);
    const cartProduct = (cart?.data?.items || [])
      .find(item => (item.id || item.productId) === prodId);
    const stockValue = 'stockValue' in (cartProduct || {})
      ? cartProduct.stockValue
      : product.stockValue;

    const quantity = Math.min(stockValue, (cartProduct?.quantity || 0) + product.quantity);
    if (
      stockValue === 0
      || quantity === cartProduct?.quantity
    ) {
      toast.addNotification({
        type: 'warning',
        title: t('products.insufficientStock')
      });
      return null;
    }

    this.setState({ addToCartLoading: true });
    try {
      const [updated, crossProducts] = await Promise.allSettled([
        cart.updateProduct({
          ...product,
          id: prodId,
          quantity
        }),
        fetch(`${ API_PRODUCTS }/${ prodId }/cross-sells?currency=${ defaultCurrency }&vat=${ withVat }`)
      ]);
      if (updated.status === 'fulfilled') {
        if (updated.value === null) {
          toast.addNotification({
            type: 'warning',
            title: t('products.dropShipmentWarning')
          });
        } else {
          toast.addNotification({
            title: t('cart.successfullyAddedToCart')
          });
        }
      } else {
        toast.addNotification({
          type: 'error',
          title: t('cart.failedAddToCart'),
          message: updated.value?.errors?.at(0)?.message
        });
        return null;
      }
      if (
        Array.isArray(crossProducts.value)
        && crossProducts.value.length
      ) {
        modalsContext.setState(modalsContext.modalState.CROSS_SALES.key, {
          open: true,
          products: crossProducts.value,
          product
        });
      }
      return updated.value;
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: t('cart.failedAddToCart'),
        message: err?.info?.errors?.at(0)?.message
      });
    } finally {
      this.setState({ addToCartLoading: false });
    }
    return null;
  }

  render() {
    const { product, addToCartLoading, loading } = this.state;
    const { parentWishlist, defaultCurrency, withVat } = this.props;
    return (
      <ProductWishlistItemComponent
        loading={ loading }
        addToCartLoading={ addToCartLoading }
        wishlist={ parentWishlist }
        onMoveToWishlist={ this.handleOnMoveToWishlist }
        product={ product }
        onStockAlertModalOpen={ this.handleStockAlertModalOpen }
        onAddToCart={ () => this.handleOnAddToCart(defaultCurrency, withVat) }
        onChange={ this.handleOnChangeQuantity }
        onDelete={ this.handleOnDelete }
        defaultCurrency={ defaultCurrency }
        withVat={ withVat }
      />
    );
  }
}

ProductWishlistItem.propTypes = {
  t: func,
  product: object,
  parentWishlist: object,
  toast: object,
  cart: object,
  wishlists: object,
  wishlistContext: object,
  defaultCurrency: string,
  withVat: bool
};

ProductWishlistItem.displayName = 'ProductWishlistItemContainer';

export default withTranslation()(
  withToastNotifications(
    withCart(
      withWishlist(
        withModals(
          withAnalytics(
            withFetch(ProductWishlistItem)
          )
        )
      )
    )
  )
);
